import { useRef } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Button from '../../components/button/Button';
import Table from '../../components/table/table';
import type { SyndicatedPreviewInstallmentRow } from '../../utils/syndicatedLines';

const formatPreviewNumber = (amount: number | null | undefined) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '0';
  return amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPreviewDate = (dateString: string) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return dateString;
  return `${day}/${month}/${year.slice(-2)}`;
};

export interface ManualInstallmentPlanSectionProps {
  canUpload: boolean;
  loading: boolean;
  downloading: boolean;
  summary: string | null;
  previewRows: SyndicatedPreviewInstallmentRow[];
  errors: string[];
  warnings: string[];
  onDownloadTemplate: () => void;
  onFileSelect: (file: File) => void;
}

const ManualInstallmentPlanSection = ({
  canUpload,
  loading,
  downloading,
  summary,
  previewRows,
  errors,
  warnings,
  onDownloadTemplate,
  onFileSelect,
}: ManualInstallmentPlanSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const previewColumns = [
    {
      name: 'N° Cuota',
      selector: (row: SyndicatedPreviewInstallmentRow) => row.installment_number,
      width: '10%',
    },
    {
      name: 'Vencimiento',
      selector: (row: SyndicatedPreviewInstallmentRow) => formatPreviewDate(row.due_date),
      width: '14%',
    },
    {
      name: 'Amortización',
      selector: (row: SyndicatedPreviewInstallmentRow) => formatPreviewNumber(row.amortization),
      width: '14%',
    },
    {
      name: 'Interés',
      selector: (row: SyndicatedPreviewInstallmentRow) => formatPreviewNumber(row.interest),
      width: '14%',
    },
    {
      name: 'Total Cuota',
      selector: (row: SyndicatedPreviewInstallmentRow) => formatPreviewNumber(row.amount),
      width: '14%',
    },
    {
      name: 'Saldo Final',
      selector: (row: SyndicatedPreviewInstallmentRow) => formatPreviewNumber(row.final_balance),
      width: '14%',
    },
  ];

  const uploadTooltip = !canUpload
    ? 'Ingresá el monto bruto antes de subir el plan de cuotas.'
    : undefined;

  return (
    <Card margin={{ bottom: '4' }} data-testid="manual-installment-plan-section">
      <div
        style={{
          backgroundColor: '#E6F5F0',
          padding: '12px 16px',
          borderRadius: '8px',
          margin: '0 0 16px 0',
        }}
      >
        <H variant="secondary-2" type="4" weight="bold" modifier="strong" withMargin={false}>
          Plan de cuotas
        </H>
      </div>

      <Card display="flex" flexDirection="column" gap="12px">
        <P type="3" withMargin={false}>
          Descargá la plantilla, completá la hoja plan_cuotas y subí el archivo (.xlsx).
        </P>
        <Card display="flex" gap="12px" flexWrap="wrap">
          <Button
            variant="secondary"
            onClick={() => void onDownloadTemplate()}
            disabled={downloading || loading}
            testId="btn-download-loan-installment-template"
          >
            {downloading ? 'Descargando...' : 'Descargar plantilla'}
          </Button>
          <Button
            variant="primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={!canUpload || loading || downloading}
            testId="btn-upload-loan-installment-plan"
            title={uploadTooltip}
          >
            {loading ? 'Validando...' : 'Subir plan de cuotas'}
          </Button>
        </Card>
        {!canUpload ? (
          <P type="4" variant="neutral" withMargin={false} data-testid="upload-blocked-help">
            {uploadTooltip}
          </P>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          data-testid="manual-installment-file-input"
          onChange={(event) => {
            const selected = event.target.files?.[0];
            event.target.value = '';
            if (selected) onFileSelect(selected);
          }}
        />
        {summary ? <P withMargin={false}>{summary}</P> : null}
        {errors.length > 0 ? (
          <Card
            margin={{ top: '2' }}
            padding="3"
            data-testid="manual-installment-error-panel"
            style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px' }}
          >
            <P weight="bold" withMargin={false} variant="danger">
              Errores en el archivo
            </P>
            {errors.map((error) => (
              <P key={error} withMargin={false} variant="danger" type="4">
                {error}
              </P>
            ))}
          </Card>
        ) : null}
        {previewRows.length > 0 ? (
          <Card margin={{ top: '2' }}>
            <div data-testid="manual-installment-preview-grid">
              <Table
                columns={previewColumns}
                data={previewRows}
                tableData={{
                  size: previewRows.length,
                  page: 0,
                  totalElements: previewRows.length,
                  totalPages: 1,
                }}
                handleChangePage={() => {}}
                variant="normal"
                disablePagination
              />
            </div>
          </Card>
        ) : null}
        {warnings.map((warning) => (
          <P key={warning} withMargin={false} type="4">
            {warning}
          </P>
        ))}
      </Card>
    </Card>
  );
};

export default ManualInstallmentPlanSection;
